import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Alert,
  RefreshControl,
  Animated,
  BackHandler,
  Image,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return config;
}, (e) => Promise.reject(e));

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

// Default export for the CompanyAdvancedAnalyticsBIScreen component
export default function CompanyAdvancedAnalyticsBIScreen({ navigation, route }) {
  // State management - COORDINATED WITH BUSINESSDASHBOARD
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('month'); // week, month, quarter, year
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    initializeScreen();
    
    // Handle hardware back button
    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      handleBackPress
    );
    
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 500,
      useNativeDriver: true,
    }).start();
    
    return () => backHandler.remove();
  }, []);

  // Handle back button press
  const handleBackPress = () => {
    if (showExportModal) {
      setShowExportModal(false);
      return true;
    }
    return false;
  };

  // Initialize screen data - API-FIRST with cache fallback
  const initializeScreen = async () => {
    try {
      setIsLoading(true);

      // ── 1. Resolve company ID ──
      const bizId = await getBusinessId();
      if (bizId) setCompanyId(bizId);
      
      // ── 2. Load user data ──
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        try { setUser(JSON.parse(userData)); } catch {}
      }
      
      // ── 3. Load company profile (API → cache → default) ──
      let profile = null;
      // Cache warm-start
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        try {
          profile = JSON.parse(cachedProfile);
          setCompanyProfile(profile);
          setCompanyLogo(profile?.logo || profile?.logo_url || profile?.company_logo || profile?.banner_image_url || null);
        } catch {}
      }
      // Fetch fresh from API
      if (bizId) {
        try {
          const res = await api.get(`/api/company-profile/${parseInt(bizId) || 0}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              profile = raw;
              setCompanyProfile(raw);
              setCompanyLogo(raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url || null);
              await AsyncStorage.setItem('company_profile', JSON.stringify(raw));
            }
          }
        } catch (apiErr) {
          console.warn('Company profile API error, using cached:', apiErr?.message);
        }
      }
      // Final fallback
      if (!profile) {
        profile = {
          name: 'JOBENG Construction Ltd',
          company_name: 'JOBENG Construction Ltd',
          industry: 'Construction & Engineering',
          headquarters: 'London, UK',
          founded: 2015,
          employee_count: 85,
          description: 'Leading construction and engineering company.',
          logo: null,
        };
        setCompanyProfile(profile);
      }

      // ── 4. Load subscription (API → cache → default) ──
      if (bizId) {
        try {
          const subRes = await api.get('/api/business-subscriptions', {
            params: { company_id: parseInt(bizId) || 0, status: 'active' },
          });
          if (subRes.data?.success && subRes.data.data) {
            const subData = Array.isArray(subRes.data.data) ? subRes.data.data[0] : subRes.data.data;
            if (subData) {
              setSubscription(subData);
              await AsyncStorage.setItem('business_subscription', JSON.stringify(subData));
            }
          }
        } catch {
          // fall through to cache
        }
      }
      if (!subscription) {
        const cachedSub = await AsyncStorage.getItem('business_subscription');
        if (cachedSub) {
          try { setSubscription(JSON.parse(cachedSub)); } catch {}
        }
      }
      
      // ── 5. Load analytics data (API → mock) ──
      await loadAnalyticsData(bizId);
      
    } catch (error) {
      console.error('Error initializing screen:', error);
      Alert.alert('Error', 'Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await loadAnalyticsData(companyId);
    setRefreshing(false);
  };

  // Load analytics data based on time range — API first, mock fallback
  const loadAnalyticsData = async (bizId) => {
    try {
      const resolvedId = bizId || companyId;

      // ✅ Try API endpoints for real data
      if (resolvedId) {
        try {
          const bId = parseInt(resolvedId) || 0;
          const [projectsRes, clientsRes, revenueRes, employeesRes] = await Promise.allSettled([
            api.get('/api/projects', { params: { company_id: bId } }).catch(() => ({ data: { success: false } })),
            api.get('/api/clients', { params: { company_id: bId } }).catch(() => ({ data: { success: false } })),
            api.get('/api/financial/revenue', { params: { company_id: bId } }).catch(() => ({ data: { success: false } })),
            api.get('/api/employees', { params: { company_id: bId } }).catch(() => ({ data: { success: false } })),
          ]);

          const projectCount = projectsRes.status === 'fulfilled' && projectsRes.value.data?.success ? projectsRes.value.data.data?.length || 0 : 0;
          const clientCount = clientsRes.status === 'fulfilled' && clientsRes.value.data?.success ? clientsRes.value.data.data?.length || 0 : 0;
          const revenueAmount = revenueRes.status === 'fulfilled' && revenueRes.value.data?.success && revenueRes.value.data.data?.length > 0
            ? parseFloat(revenueRes.value.data.data[0].amount || 0) : 0;
          const employeeCount = employeesRes.status === 'fulfilled' && employeesRes.value.data?.success ? employeesRes.value.data.data?.length || 0 : 0;

          // Build team performance from real employee data
          let teamPerformanceData = [];
          if (employeesRes.status === 'fulfilled' && employeesRes.value.data?.success && Array.isArray(employeesRes.value.data.data)) {
            teamPerformanceData = employeesRes.value.data.data.slice(0, 5).map((emp) => ({
              member: emp.full_name || emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Employee',
              projects: Math.floor(Math.random() * 12) + 3,
              completion: Math.floor(Math.random() * 15) + 85,
              rating: (Math.random() * 0.5 + 4.4).toFixed(1),
            }));
          }

          // If we got any real data, use it to augment the dashboard
          if (projectCount > 0 || clientCount > 0 || revenueAmount > 0) {
            console.log(`✅ Analytics loaded from API: ${projectCount} projects, ${clientCount} clients, £${revenueAmount} revenue`);
          }

          // Merge API data into the mock structure
          const overview = {
            totalRevenue: revenueAmount || baseDataForRange(selectedTimeRange).totalRevenue,
            revenueGrowth: baseDataForRange(selectedTimeRange).revenueGrowth,
            totalProjects: projectCount || baseDataForRange(selectedTimeRange).totalProjects,
            projectsGrowth: baseDataForRange(selectedTimeRange).projectsGrowth,
            activeClients: clientCount || baseDataForRange(selectedTimeRange).activeClients,
            clientsGrowth: baseDataForRange(selectedTimeRange).clientsGrowth,
            teamProductivity: baseDataForRange(selectedTimeRange).teamProductivity,
            productivityGrowth: baseDataForRange(selectedTimeRange).productivityGrowth,
          };

          setAnalyticsData({
            overview,
            revenueByMonth: buildRevenueByMonth(revenueAmount),
            projectsByStatus: buildProjectsByStatus(projectCount),
            topPerformingServices: buildTopServices(companyProfile),
            clientSatisfaction: buildClientSatisfaction(),
            teamPerformance: teamPerformanceData.length > 0 ? teamPerformanceData : buildDefaultTeam(),
          });
          return;
        } catch (apiErr) {
          console.warn('Analytics API fetch failed, using mock:', apiErr?.message);
        }
      }

      // Fallback: full mock data
      setAnalyticsData({
        overview: baseDataForRange(selectedTimeRange),
        revenueByMonth: buildRevenueByMonth(0),
        projectsByStatus: buildProjectsByStatus(0),
        topPerformingServices: buildTopServices(companyProfile),
        clientSatisfaction: buildClientSatisfaction(),
        teamPerformance: buildDefaultTeam(),
      });
    } catch (error) {
      console.error('Error loading analytics data:', error);
      Alert.alert('Error', 'Failed to load analytics data');
    }
  };

  // ── Analytics data builders ──
  const baseDataForRange = (range) => {
    const map = {
      week: { totalRevenue: 28500, revenueGrowth: 12.5, totalProjects: 8, projectsGrowth: 14.3, activeClients: 12, clientsGrowth: 8.3, teamProductivity: 89.2, productivityGrowth: 6.1 },
      month: { totalRevenue: 125000, revenueGrowth: 15.3, totalProjects: 48, projectsGrowth: 8.5, activeClients: 32, clientsGrowth: 12.1, teamProductivity: 87.5, productivityGrowth: 5.2 },
      quarter: { totalRevenue: 365000, revenueGrowth: 18.7, totalProjects: 142, projectsGrowth: 11.2, activeClients: 85, clientsGrowth: 15.8, teamProductivity: 86.3, productivityGrowth: 4.9 },
      year: { totalRevenue: 1450000, revenueGrowth: 22.4, totalProjects: 568, projectsGrowth: 13.6, activeClients: 245, clientsGrowth: 19.3, teamProductivity: 85.8, productivityGrowth: 7.2 },
    };
    return map[range] || map.month;
  };

  const buildRevenueByMonth = (apiRevenue) => {
    const base = apiRevenue > 0 ? apiRevenue / 6 : 10000;
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map((month) => ({
      month,
      revenue: Math.round(base * (0.8 + Math.random() * 0.4)),
    }));
  };

  const buildProjectsByStatus = (totalFromApi) => {
    const total = totalFromApi || 48;
    return [
      { status: 'Completed', count: Math.round(total * 0.58), percentage: 58.3, color: '#4CAF50' },
      { status: 'In Progress', count: Math.round(total * 0.31), percentage: 31.3, color: '#2196F3' },
      { status: 'On Hold', count: Math.round(total * 0.10), percentage: 10.4, color: '#FF9800' },
    ];
  };

  const buildTopServices = (profile) => {
    const specs = profile?.specializations || ['Structural Engineering', 'MEP Design', 'Project Management', 'Quality Assurance'];
    return specs.slice(0, 4).map((service, i) => ({
      service,
      revenue: Math.round(45000 - i * 8000 + Math.random() * 5000),
      projects: Math.round(12 - i * 2 + Math.random() * 3),
      growth: +(15 - i * 2 + Math.random() * 5).toFixed(1),
    }));
  };

  const buildClientSatisfaction = () => ({
    averageRating: 4.7,
    totalReviews: 156,
    distribution: [
      { stars: 5, count: 98, percentage: 62.8 },
      { stars: 4, count: 42, percentage: 26.9 },
      { stars: 3, count: 12, percentage: 7.7 },
      { stars: 2, count: 3, percentage: 1.9 },
      { stars: 1, count: 1, percentage: 0.6 },
    ],
  });

  const buildDefaultTeam = () => [
    { member: 'John Smith', projects: 12, completion: 95, rating: 4.8 },
    { member: 'Sarah Johnson', projects: 10, completion: 92, rating: 4.7 },
    { member: 'Mike Wilson', projects: 8, completion: 88, rating: 4.6 },
    { member: 'Emily Davis', projects: 7, completion: 94, rating: 4.9 },
  ];

  // Reload data when time range changes
  useEffect(() => {
    if (!isLoading) {
      loadAnalyticsData(companyId);
    }
  }, [selectedTimeRange]);

  // Check if feature is available based on subscription - SAME AS BUSINESSDASHBOARD
  const isFeatureAvailable = (requiredPlan) => {
    if (!subscription) return false;
    
    const planHierarchy = {
      'single_starter': 1,
      'single_professional': 2,
      'single_enterprise': 3,
      'multi_team': 2,
      'multi_business': 3,
      'multi_enterprise': 4
    };
    
    const requiredPlanMap = {
      'starter': 1,
      'professional': 2,
      'enterprise': 3,
      'team': 2
    };
    
    return planHierarchy[subscription.plan_id] >= requiredPlanMap[requiredPlan];
  };

  // Navigation helper with safety checks
  const navigateToScreen = (screenName, params = {}) => {
    try {
      if (navigation && navigation.navigate) {
        navigation.navigate(screenName, {
          ...params,
          companyProfile,
          subscription,
          companyId,
        });
      }
    } catch (error) {
      console.error('Navigation error:', error);
      Alert.alert('Navigation Error', 'Unable to navigate to the requested screen.');
    }
  };

  // Time range options
  const timeRanges = [
    { id: 'week', label: 'Week', icon: 'calendar-outline' },
    { id: 'month', label: 'Month', icon: 'calendar' },
    { id: 'quarter', label: 'Quarter', icon: 'calendar-range' },
    { id: 'year', label: 'Year', icon: 'calendar-today' },
  ];

  // Key metrics cards
  const renderKeyMetrics = () => {
    if (!analyticsData) return null;

    const metrics = [
      {
        id: 'revenue',
        title: 'Total Revenue',
        value: `£${analyticsData.overview.totalRevenue.toLocaleString()}`,
        growth: analyticsData.overview.revenueGrowth,
        icon: 'cash',
        color: '#4CAF50',
      },
      {
        id: 'projects',
        title: 'Total Projects',
        value: analyticsData.overview.totalProjects.toString(),
        growth: analyticsData.overview.projectsGrowth,
        icon: 'briefcase',
        color: '#2196F3',
      },
      {
        id: 'clients',
        title: 'Active Clients',
        value: analyticsData.overview.activeClients.toString(),
        growth: analyticsData.overview.clientsGrowth,
        icon: 'people',
        color: '#FF9800',
      },
      {
        id: 'productivity',
        title: 'Team Productivity',
        value: `${analyticsData.overview.teamProductivity}%`,
        growth: analyticsData.overview.productivityGrowth,
        icon: 'trending-up',
        color: '#9C27B0',
      },
    ];

    return (
      <View style={styles.metricsGrid}>
        {metrics.map(metric => (
          <TouchableOpacity
            key={metric.id}
            style={styles.metricCard}
            onPress={() => setSelectedMetric(metric)}
            activeOpacity={0.7}
          >
            <View style={[styles.metricIconContainer, { backgroundColor: `${metric.color}20` }]}>
              <Ionicons name={metric.icon} size={24} color={metric.color} />
            </View>
            <Text style={styles.metricTitle}>{metric.title}</Text>
            <Text style={styles.metricValue}>{metric.value}</Text>
            <View style={styles.metricGrowth}>
              <Ionicons 
                name={metric.growth >= 0 ? 'arrow-up' : 'arrow-down'} 
                size={14} 
                color={metric.growth >= 0 ? '#4CAF50' : '#ef4444'} 
              />
              <Text style={[styles.metricGrowthText, { color: metric.growth >= 0 ? '#4CAF50' : '#ef4444' }]}>
                {Math.abs(metric.growth).toFixed(1)}%
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // Revenue chart visualization
  const renderRevenueChart = () => {
    if (!analyticsData || !analyticsData.revenueByMonth) return null;

    const maxRevenue = Math.max(...analyticsData.revenueByMonth.map(item => item.revenue));

    return (
      <View style={styles.chartSection}>
        <View style={styles.chartHeader}>
          <Text style={styles.chartTitle}>Revenue Trend</Text>
          <TouchableOpacity 
            style={styles.exportButton}
            onPress={() => setShowExportModal(true)}
          >
            <Ionicons name="download-outline" size={18} color="#4A90E2" />
            <Text style={styles.exportButtonText}>Export</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.chartContainer}>
          {analyticsData.revenueByMonth.map((item, index) => {
            const barHeight = (item.revenue / maxRevenue) * 150;
            return (
              <View key={index} style={styles.chartBar}>
                <View style={styles.chartBarValue}>
                  <Text style={styles.chartBarValueText}>£{(item.revenue / 1000).toFixed(1)}k</Text>
                </View>
                <View style={[styles.chartBarFill, { height: barHeight, backgroundColor: '#4A90E2' }]} />
                <Text style={styles.chartBarLabel}>{item.month}</Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  // Projects by status
  const renderProjectsStatus = () => {
    if (!analyticsData || !analyticsData.projectsByStatus) return null;

    return (
      <View style={styles.statusSection}>
        <Text style={styles.sectionTitle}>Projects by Status</Text>
        
        {analyticsData.projectsByStatus.map((item, index) => (
          <View key={index} style={styles.statusItem}>
            <View style={styles.statusInfo}>
              <View style={[styles.statusDot, { backgroundColor: item.color }]} />
              <Text style={styles.statusLabel}>{item.status}</Text>
            </View>
            <View style={styles.statusStats}>
              <Text style={styles.statusCount}>{item.count}</Text>
              <Text style={styles.statusPercentage}>{item.percentage.toFixed(1)}%</Text>
            </View>
            <View style={styles.statusBarContainer}>
              <View style={[styles.statusBar, { width: `${item.percentage}%`, backgroundColor: item.color }]} />
            </View>
          </View>
        ))}
      </View>
    );
  };

  // Top performing services
  const renderTopServices = () => {
    if (!analyticsData || !analyticsData.topPerformingServices) return null;

    return (
      <View style={styles.servicesSection}>
        <Text style={styles.sectionTitle}>Top Performing Services</Text>
        
        {analyticsData.topPerformingServices.map((item, index) => (
          <View key={index} style={styles.serviceItem}>
            <View style={styles.serviceRank}>
              <Text style={styles.serviceRankText}>{index + 1}</Text>
            </View>
            <View style={styles.serviceInfo}>
              <Text style={styles.serviceName}>{item.service}</Text>
              <View style={styles.serviceDetails}>
                <Text style={styles.serviceProjects}>{item.projects} projects</Text>
                <View style={styles.serviceGrowth}>
                  <Ionicons 
                    name="trending-up" 
                    size={12} 
                    color="#4CAF50" 
                  />
                  <Text style={styles.serviceGrowthText}>+{item.growth}%</Text>
                </View>
              </View>
            </View>
            <View style={styles.serviceRevenue}>
              <Text style={styles.serviceRevenueText}>£{item.revenue.toLocaleString()}</Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  // Client satisfaction
  const renderClientSatisfaction = () => {
    if (!analyticsData || !analyticsData.clientSatisfaction) return null;

    const satisfaction = analyticsData.clientSatisfaction;

    return (
      <View style={styles.satisfactionSection}>
        <Text style={styles.sectionTitle}>Client Satisfaction</Text>
        
        <View style={styles.satisfactionOverview}>
          <View style={styles.satisfactionRating}>
            <Text style={styles.satisfactionScore}>{satisfaction.averageRating}</Text>
            <View style={styles.satisfactionStars}>
              {[1, 2, 3, 4, 5].map(star => (
                <Ionicons 
                  key={star}
                  name={star <= Math.floor(satisfaction.averageRating) ? 'star' : 'star-outline'}
                  size={20}
                  color="#FFB800"
                />
              ))}
            </View>
            <Text style={styles.satisfactionReviews}>{satisfaction.totalReviews} reviews</Text>
          </View>
          
          <View style={styles.satisfactionDistribution}>
            {satisfaction.distribution.map((item, index) => (
              <View key={index} style={styles.satisfactionRow}>
                <Text style={styles.satisfactionStarLabel}>{item.stars}★</Text>
                <View style={styles.satisfactionBarContainer}>
                  <View style={[styles.satisfactionBar, { width: `${item.percentage}%` }]} />
                </View>
                <Text style={styles.satisfactionPercentage}>{item.percentage.toFixed(1)}%</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  // Team performance
  const renderTeamPerformance = () => {
    if (!analyticsData || !analyticsData.teamPerformance) return null;

    return (
      <View style={styles.teamSection}>
        <Text style={styles.sectionTitle}>Team Performance</Text>
        
        {analyticsData.teamPerformance.map((member, index) => (
          <View key={index} style={styles.teamMemberItem}>
            <View style={styles.teamMemberAvatar}>
              <Text style={styles.teamMemberInitials}>
                {member.member.split(' ').map(n => n[0]).join('')}
              </Text>
            </View>
            <View style={styles.teamMemberInfo}>
              <Text style={styles.teamMemberName}>{member.member}</Text>
              <View style={styles.teamMemberStats}>
                <View style={styles.teamStat}>
                  <Ionicons name="briefcase-outline" size={12} color="#64748b" />
                  <Text style={styles.teamStatText}>{member.projects} projects</Text>
                </View>
                <View style={styles.teamStat}>
                  <Ionicons name="checkmark-circle-outline" size={12} color="#64748b" />
                  <Text style={styles.teamStatText}>{member.completion}% complete</Text>
                </View>
              </View>
            </View>
            <View style={styles.teamMemberRating}>
              <Ionicons name="star" size={14} color="#FFB800" />
              <Text style={styles.teamMemberRatingText}>{member.rating}</Text>
            </View>
          </View>
        ))}
      </View>
    );
  };

  // Advanced analytics features
  const advancedFeatures = [
    {
      id: 'predictive',
      title: 'Predictive Analytics',
      description: 'AI-powered forecasting for revenue and project trends',
      icon: 'analytics',
      color: '#4CAF50',
      screen: 'PredictiveAnalyticsScreen',
      requiredPlan: 'enterprise',
    },
    {
      id: 'custom_reports',
      title: 'Custom Reports',
      description: 'Create and schedule custom business reports',
      icon: 'document-text',
      color: '#2196F3',
      screen: 'CustomReportsScreen',
      requiredPlan: 'professional',
    },
    {
      id: 'data_export',
      title: 'Data Export & Integration',
      description: 'Export data to Excel, PDF, or integrate with BI tools',
      icon: 'cloud-download',
      color: '#FF9800',
      screen: 'DataExportScreen',
      requiredPlan: 'professional',
    },
    {
      id: 'benchmarking',
      title: 'Industry Benchmarking',
      description: 'Compare your performance with industry standards',
      icon: 'bar-chart',
      color: '#9C27B0',
      screen: 'BenchmarkingScreen',
      requiredPlan: 'enterprise',
    },
  ];

  const renderAdvancedFeatures = () => {
    return (
      <View style={styles.featuresSection}>
        <Text style={styles.sectionTitle}>Advanced Features</Text>
        
        {advancedFeatures.map(feature => {
          const available = isFeatureAvailable(feature.requiredPlan);
          
          return (
            <TouchableOpacity
              key={feature.id}
              style={[styles.featureCard, !available && styles.lockedFeatureCard]}
              onPress={() => {
                if (available) {
                  navigateToScreen(feature.screen);
                } else {
                  Alert.alert(
                    'Upgrade Required',
                    `This feature requires ${feature.requiredPlan} plan or higher. Would you like to upgrade?`,
                    [
                      { text: 'Cancel', style: 'cancel' },
                      { 
                        text: 'Upgrade', 
                        onPress: () => navigateToScreen('BusinessSubscriptionSystem', { 
                          currentPlan: subscription 
                        })
                      }
                    ]
                  );
                }
              }}
              activeOpacity={0.7}
            >
              <View style={[styles.featureIconContainer, { backgroundColor: `${feature.color}20` }]}>
                <Ionicons 
                  name={feature.icon} 
                  size={24} 
                  color={available ? feature.color : '#94A3B8'} 
                />
                {!available && (
                  <View style={styles.featureLockOverlay}>
                    <Ionicons name="lock-closed" size={12} color="#fff" />
                  </View>
                )}
              </View>
              <View style={styles.featureContent}>
                <View style={styles.featureHeader}>
                  <Text style={[styles.featureTitle, !available && styles.lockedFeatureTitle]}>
                    {feature.title}
                  </Text>
                  {!available && (
                    <View style={styles.planRequiredBadge}>
                      <Text style={styles.planRequiredText}>
                        {String(feature.requiredPlan ?? '').toUpperCase()}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={[styles.featureDescription, !available && styles.lockedFeatureDescription]}>
                  {feature.description}
                </Text>
              </View>
              <Ionicons 
                name="chevron-forward" 
                size={20} 
                color={available ? feature.color : '#94A3B8'} 
              />
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Analytics...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <LinearGradient
        colors={['#4A90E2', '#357ABD']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={() => navigation && navigation.goBack && navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          
          <View style={styles.headerTitleContainer}>
            {companyLogo ? (
              <Image source={{ uri: companyLogo || undefined }} style={{ width: 32, height: 32, borderRadius: 16, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
            ) : (
              <MaterialCommunityIcons name="chart-line" size={28} color="#fff" />
            )}
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerTitle}>Advanced Analytics & BI</Text>
              <Text style={styles.headerSubtitle}>
                {companyProfile?.company_name || companyProfile?.name || 'Business Intelligence Dashboard'}
              </Text>
            </View>
          </View>
          
          <TouchableOpacity 
            style={styles.settingsButton}
            onPress={() => setShowExportModal(true)}
          >
            <Ionicons name="settings-outline" size={24} color="#fff" />
          </TouchableOpacity>
        </View>
        
        {/* Subscription Badge */}
        {subscription && (
          <View style={styles.subscriptionBadge}>
            <MaterialCommunityIcons name="crown" size={14} color="#FFD700" />
            <Text style={styles.subscriptionText}>
              {subscription.plan_id ? 
                subscription.plan_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                'Premium'
              } Plan
            </Text>
          </View>
        )}
      </LinearGradient>

      <Animated.ScrollView 
        style={[styles.scrollView, { opacity: fadeAnim }]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#4A90E2']}
            tintColor="#4A90E2"
          />
        }
      >
        {/* Time Range Selector */}
        <View style={styles.timeRangeSection}>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.timeRangeContainer}
          >
            {timeRanges.map(range => (
              <TouchableOpacity
                key={range.id}
                style={[
                  styles.timeRangeButton,
                  selectedTimeRange === range.id && styles.timeRangeButtonActive
                ]}
                onPress={() => setSelectedTimeRange(range.id)}
                activeOpacity={0.7}
              >
                <Ionicons 
                  name={range.icon} 
                  size={18} 
                  color={selectedTimeRange === range.id ? '#fff' : '#64748b'} 
                />
                <Text style={[
                  styles.timeRangeText,
                  selectedTimeRange === range.id && styles.timeRangeTextActive
                ]}>
                  {range.label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Key Metrics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Performance Indicators</Text>
          {renderKeyMetrics()}
        </View>

        {/* Revenue Chart */}
        {renderRevenueChart()}

        {/* Projects Status */}
        {renderProjectsStatus()}

        {/* Top Services */}
        {renderTopServices()}

        {/* Client Satisfaction */}
        {renderClientSatisfaction()}

        {/* Team Performance */}
        {renderTeamPerformance()}

        {/* Advanced Features */}
        {renderAdvancedFeatures()}

        {/* Bottom Spacing */}
        <View style={styles.bottomSpacing} />
      </Animated.ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 16,
  },
  headerTextContainer: {
    marginLeft: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  settingsButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  subscriptionBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginTop: 12,
    alignSelf: 'flex-start',
    gap: 6,
  },
  subscriptionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  timeRangeSection: {
    backgroundColor: '#fff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  timeRangeContainer: {
    paddingHorizontal: 20,
    gap: 12,
  },
  timeRangeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: '#f8fafc',
    gap: 8,
  },
  timeRangeButtonActive: {
    backgroundColor: '#4A90E2',
  },
  timeRangeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748b',
  },
  timeRangeTextActive: {
    color: '#fff',
  },
  section: {
    paddingHorizontal: 20,
    paddingTop: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  metricCard: {
    width: (width - 52) / 2,
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  metricIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  metricTitle: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 8,
  },
  metricGrowth: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricGrowthText: {
    fontSize: 12,
    fontWeight: '600',
  },
  chartSection: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginTop: 24,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#f0f7ff',
  },
  exportButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4A90E2',
  },
  chartContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 180,
  },
  chartBar: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginHorizontal: 4,
  },
  chartBarValue: {
    marginBottom: 4,
  },
  chartBarValueText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#64748b',
  },
  chartBarFill: {
    width: '100%',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minHeight: 20,
  },
  chartBarLabel: {
    fontSize: 10,
    color: '#64748b',
    marginTop: 8,
  },
  statusSection: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginTop: 24,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  statusItem: {
    marginBottom: 16,
  },
  statusInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 8,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    flex: 1,
  },
  statusStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  statusCount: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  statusPercentage: {
    fontSize: 12,
    color: '#64748b',
  },
  statusBarContainer: {
    height: 8,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    overflow: 'hidden',
  },
  statusBar: {
    height: '100%',
    borderRadius: 4,
  },
  servicesSection: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginTop: 24,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  serviceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  serviceRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f1f5f9',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  serviceRankText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#64748b',
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  serviceDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  serviceProjects: {
    fontSize: 12,
    color: '#64748b',
  },
  serviceGrowth: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  serviceGrowthText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#4CAF50',
  },
  serviceRevenue: {
    alignItems: 'flex-end',
  },
  serviceRevenueText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  satisfactionSection: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginTop: 24,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  satisfactionOverview: {
    flexDirection: 'row',
    gap: 20,
  },
  satisfactionRating: {
    alignItems: 'center',
    paddingRight: 20,
    borderRightWidth: 1,
    borderRightColor: '#e2e8f0',
  },
  satisfactionScore: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 8,
  },
  satisfactionStars: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 8,
  },
  satisfactionReviews: {
    fontSize: 12,
    color: '#64748b',
  },
  satisfactionDistribution: {
    flex: 1,
    justifyContent: 'center',
  },
  satisfactionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  satisfactionStarLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748b',
    width: 24,
  },
  satisfactionBarContainer: {
    flex: 1,
    height: 8,
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    overflow: 'hidden',
  },
  satisfactionBar: {
    height: '100%',
    backgroundColor: '#FFB800',
    borderRadius: 4,
  },
  satisfactionPercentage: {
    fontSize: 11,
    color: '#64748b',
    width: 40,
    textAlign: 'right',
  },
  teamSection: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginTop: 24,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  teamMemberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  teamMemberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  teamMemberInitials: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#fff',
  },
  teamMemberInfo: {
    flex: 1,
  },
  teamMemberName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 4,
  },
  teamMemberStats: {
    flexDirection: 'row',
    gap: 12,
  },
  teamStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  teamStatText: {
    fontSize: 11,
    color: '#64748b',
  },
  teamMemberRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#FFF9E6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  teamMemberRatingText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#92400e',
  },
  featuresSection: {
    paddingHorizontal: 20,
    marginTop: 24,
  },
  featureCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  lockedFeatureCard: {
    opacity: 0.6,
  },
  featureIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    position: 'relative',
  },
  featureLockOverlay: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  featureContent: {
    flex: 1,
  },
  featureHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  featureTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1e293b',
    flex: 1,
  },
  lockedFeatureTitle: {
    color: '#94a3b8',
  },
  planRequiredBadge: {
    backgroundColor: '#fef3c7',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    marginLeft: 8,
  },
  planRequiredText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#92400e',
  },
  featureDescription: {
    fontSize: 13,
    color: '#64748b',
    lineHeight: 18,
  },
  lockedFeatureDescription: {
    color: '#94a3b8',
  },
  bottomSpacing: {
    height: 32,
  },
});